import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Edit3, ExternalLink, Plus, RefreshCw, Rocket, Sparkles, Trash2, Users } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import VerifiedBadge from "@/components/VerifiedBadge";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import UpgradeDialog from "@/components/UpgradeDialog";
import EditServerDialog from "@/components/EditServerDialog";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [bumping, setBumping] = useState(null);
  const [upgradeTarget, setUpgradeTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  const load = async () => {
    try {
      const { data } = await api.get("/servers/mine");
      setItems(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  const remove = async (s) => {
    setBusy(s.id);
    try {
      await api.delete(`/servers/${s.id}`);
      toast.success("Server removed");
      setItems((prev) => prev.filter((x) => x.id !== s.id));
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setBusy(null);
    }
  };

  const refresh = async (s) => {
    setBusy(s.id);
    try {
      const { data } = await api.post(`/servers/${s.id}/refresh`);
      setItems((prev) => prev.map((x) => (x.id === s.id ? { ...x, ...data } : x)));
      toast.success("Stats refreshed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    } finally {
      setBusy(null);
    }
  };

  // Bump a server directly from the dashboard. Same endpoint the site detail
  // page uses — 429 responses carry a friendly cooldown message.
  const bump = async (s) => {
    setBumping(s.id);
    try {
      const { data } = await api.post(`/servers/${s.id}/bump`);
      setItems((prev) =>
        prev.map((x) => (x.id === s.id ? { ...x, bumped_at: data.bumped_at } : x))
      );
      toast.success(`Bumped ${s.name} — now trending 🚀`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Bump failed";
      toast.error(msg);
    } finally {
      setBumping(null);
    }
  };

  if (loading || !user) return null;

  const totalReviews = items.reduce((a, s) => a + (s.review_count || 0), 0);
  const totalMembers = items.reduce((a, s) => a + (s.member_count || 0), 0);
  // Weighted average rating across all owned servers
  const totalRatingSum = items.reduce(
    (a, s) => a + (Number(s.avg_rating) || 0) * (s.review_count || 0),
    0
  );
  const avgRating = totalReviews > 0 ? (totalRatingSum / totalReviews).toFixed(1) : "—";

  return (
    <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="dashboard-page">
      <SEO title="Owner dashboard" description="Manage your Discord server listings on Serverlistr." path="/dashboard" noindex />
      <Breadcrumbs items={[{ name: "Dashboard" }]} />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-neon-cyan mb-2">Your control room</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black inline-flex items-center gap-3">
            Welcome, {user.name?.split(" ")[0]}
            <VerifiedBadge verified={user.is_verified} size={22} label={false} />
          </h1>
          <p className="mt-2 text-sm text-zinc-400">Manage your listings, refresh Discord stats, and boost visibility.</p>
        </div>
        <Link
          to="/submit"
          data-testid="dashboard-submit-btn"
          className="inline-flex items-center gap-1.5 h-11 px-5 rounded-lg bg-blurple hover:bg-blurple-hover text-white font-semibold transition-all hover:shadow-[0_0_20px_rgba(88,101,242,0.4)]"
        >
          <Plus className="w-4 h-4" /> New listing
        </Link>
      </div>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MetricCard label="Servers" value={items.length} />
        <MetricCard label="Reviews received" value={totalReviews} />
        <MetricCard label="Combined members" value={totalMembers} />
        <MetricCard label="Avg rating" value={avgRating} accent />
      </div>

      <div className="mt-10 space-y-4">
        {items.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl">
            <h3 className="font-heading text-xl font-bold">No servers yet</h3>
            <p className="mt-2 text-sm text-zinc-500">List your first Discord server to get discovered.</p>
            <Link to="/submit" className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-blurple hover:bg-blurple-hover text-white font-semibold">
              <Plus className="w-4 h-4" /> Add server
            </Link>
          </div>
        ) : (
          items.map((s) => (
            <div key={s.id} data-testid="dashboard-row" className={`rounded-2xl bg-[#0A0A0F] border border-white/5 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${s.is_featured ? "neon-border" : ""}`}>
              <img src={s.icon_url || "https://cdn.discordapp.com/embed/avatars/0.png"} alt="" className="w-14 h-14 rounded-xl object-cover bg-[#12121A]" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/servers/${s.slug || s.id}`} className="font-heading font-bold text-lg truncate hover:text-blurple">{s.name}</Link>
                  {s.is_featured && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold text-neon-cyan bg-neon-cyan/10 border border-neon-cyan/30">
                      <Sparkles className="w-3 h-3" /> FEATURED
                    </span>
                  )}
                  {s.moderation_status && s.moderation_status !== "approved" && (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${
                        s.moderation_status === "pending"
                          ? "bg-yellow-500/10 border-yellow-500/40 text-yellow-300"
                          : "bg-red-500/10 border-red-500/40 text-red-300"
                      }`}
                      data-testid={`mod-status-${s.id}`}
                    >
                      {s.moderation_status === "pending" ? "Pending review" : "Rejected"}
                    </span>
                  )}
                </div>
                {s.moderation_status && s.moderation_status !== "approved" && s.moderation_note && (
                  <div className="mt-2 rounded-lg border border-white/10 bg-white/[0.02] p-2 text-xs text-zinc-300">
                    <span className="font-semibold text-zinc-100">Note from staff: </span>
                    {s.moderation_note}
                  </div>
                )}
                <div className="mt-1 flex items-center gap-4 text-xs font-mono text-zinc-500 flex-wrap">
                  <span>
                    {s.review_count > 0
                      ? `★ ${Number(s.avg_rating || 0).toFixed(1)} · ${s.review_count} review${s.review_count === 1 ? "" : "s"}`
                      : "No reviews yet"}
                  </span>
                  <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{(s.member_count || 0).toLocaleString()}</span>
                  <span
                    data-testid={`views-30d-${s.id}`}
                    className="inline-flex items-center gap-1 text-emerald-300"
                    title="Views on your listing over the last 30 days · Total views since listed"
                  >
                    👁 {(s.views_30d || 0).toLocaleString()}
                    <span className="text-zinc-600">30d</span>
                    <span className="text-zinc-600">
                      · {(s.views_total || 0).toLocaleString()} all-time
                    </span>
                  </span>
                  {s.featured_until && <span>until {new Date(s.featured_until).toLocaleDateString()}</span>}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  data-testid={`bump-${s.id}`}
                  disabled={bumping === s.id}
                  onClick={() => bump(s)}
                  className="h-9 px-3 rounded-lg bg-blurple/15 hover:bg-blurple/25 border border-blurple/40 text-blurple text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                  title="Bump this server to the top of trending"
                >
                  <Rocket className={`w-3.5 h-3.5 ${bumping === s.id ? "animate-pulse" : ""}`} />
                  {bumping === s.id ? "Bumping..." : "Bump"}
                </button>
                <button
                  data-testid={`refresh-${s.id}`}
                  disabled={busy === s.id}
                  onClick={() => refresh(s)}
                  className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${busy === s.id ? "animate-spin" : ""}`} /> Refresh
                </button>
                <button
                  data-testid={`edit-${s.id}`}
                  onClick={() => setEditTarget(s)}
                  className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-semibold inline-flex items-center gap-1.5"
                >
                  <Edit3 className="w-3.5 h-3.5" /> Edit
                </button>
                <button
                  data-testid={`upgrade-${s.id}`}
                  onClick={() => setUpgradeTarget(s)}
                  className="h-9 px-3 rounded-lg bg-neon-cyan/10 hover:bg-neon-cyan/20 border border-neon-cyan/40 text-neon-cyan text-xs font-semibold inline-flex items-center gap-1.5"
                >
                  <Sparkles className="w-3.5 h-3.5" /> Feature
                </button>
                <a
                  href={s.invite_url}
                  target="_blank"
                  rel="noreferrer"
                  className="h-9 w-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center"
                  title="Open invite"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`delete-${s.id}`} className="h-9 w-9 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 flex items-center justify-center">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#0A0A0F] border-white/10 text-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {s.name}?</AlertDialogTitle>
                      <AlertDialogDescription className="text-zinc-400">
                        This removes the listing permanently. Your Discord server is not affected.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(s)} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))
        )}
      </div>

      <UpgradeDialog
        open={!!upgradeTarget}
        onOpenChange={(o) => !o && setUpgradeTarget(null)}
        server={upgradeTarget}
      />
      <EditServerDialog
        open={!!editTarget}
        server={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={(updated) =>
          setItems((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))
        }
      />
    </div>
  );
}

function MetricCard({ label, value, accent }) {
  const isNumeric = typeof value === "number";
  return (
    <div className={`rounded-2xl p-5 border ${accent ? "border-neon-cyan/40 bg-neon-cyan/5" : "border-white/5 bg-[#0A0A0F]"}`}>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`mt-2 font-heading text-2xl font-black ${accent ? "text-neon-cyan" : ""}`} data-testid={`metric-${label.replace(/\s/g, "-").toLowerCase()}`}>
        {isNumeric ? Number(value || 0).toLocaleString() : value}
      </div>
    </div>
  );
}
